import * as Dialog from "@radix-ui/react-dialog";
import {
  Calendar,
  FolderClosed,
  Home as HomeIcon,
  ScrollText,
  Settings as SettingsIcon,
  ShieldAlert,
  Trash2,
  Users,
  X,
} from "lucide-react";
import type { ComponentType, SVGProps } from "react";
import { NavLink } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { cn } from "../lib/cn";
import { useNavCounts } from "../lib/navCounts";

type IconType = ComponentType<SVGProps<SVGSVGElement>>;

interface NavItem {
  to: string;
  labelKey: string;
  icon: IconType;
  adminOnly?: boolean;
  auditLogOnly?: boolean;
  deletedEntitiesOnly?: boolean;
}

const NAV_ITEM_DEFS: NavItem[] = [
  { to: "/", labelKey: "nav:home", icon: HomeIcon },
  { to: "/entities", labelKey: "nav:entities", icon: FolderClosed },
  { to: "/renewals", labelKey: "nav:renewals", icon: Calendar },
  { to: "/regulatory-watch", labelKey: "nav:regulatory_watch", icon: ShieldAlert },
  { to: "/history", labelKey: "nav:history", icon: ScrollText, auditLogOnly: true },
  { to: "/admin", labelKey: "nav:admin", icon: Users, adminOnly: true },
  {
    to: "/admin/deleted-entities",
    labelKey: "nav:deleted_entities",
    icon: Trash2,
    deletedEntitiesOnly: true,
  },
  { to: "/settings", labelKey: "nav:settings", icon: SettingsIcon },
];

interface LeftNavProps {
  open: boolean;
  onClose: () => void;
  canSeeAdmin: boolean;
  canSeeAuditLog: boolean;
  canSeeDeletedEntities: boolean;
  canEdit?: boolean;
}

// ── Brand block ───────────────────────────────────────────────────────────────
// Shared between desktop sidebar and mobile drawer header.
function BrandBlock(): JSX.Element {
  return (
    // Proto .brand
    <div className="flex items-center gap-[9px] p-[14px_15px] shrink-0">
      {/* Real Hightekers symbol mark */}
      <img src="/hightekers-symbol.svg" alt="Hightekers" className="h-7 w-7 shrink-0" />
      <div className="min-w-0">
        {/* Proto .brand .name */}
        <div className="font-display font-semibold text-[14.5px] text-text leading-none truncate">
          Hightekers
        </div>
        {/* Proto .brand .sub */}
        <div className="text-[10.5px] text-dim leading-none mt-0.5">Corporate Map</div>
      </div>
    </div>
  );
}

// ── Nav list ──────────────────────────────────────────────────────────────────

function NavList({
  canSeeAdmin,
  canSeeAuditLog,
  canSeeDeletedEntities,
  onItemClick,
}: {
  canSeeAdmin: boolean;
  canSeeAuditLog: boolean;
  canSeeDeletedEntities: boolean;
  onItemClick?: () => void;
}): JSX.Element {
  const { t } = useTranslation("nav");
  const items = NAV_ITEM_DEFS.filter(
    (n) =>
      (!n.adminOnly || canSeeAdmin) &&
      (!n.auditLogOnly || canSeeAuditLog) &&
      (!n.deletedEntitiesOnly || canSeeDeletedEntities),
  );
  const counts = useNavCounts();

  // Items that belong to the compliance cluster
  const COMPLIANCE_ROUTES = new Set(["/renewals", "/regulatory-watch", "/history"]);

  return (
    <nav aria-label="Primary" className="flex flex-col gap-0">
      {items.map((it, idx) => {
        const isFirstComplianceItem = it.to === "/renewals";
        const prevTo = items[idx - 1]?.to;
        const showComplianceHeader =
          isFirstComplianceItem && (!prevTo || !COMPLIANCE_ROUTES.has(prevTo));

        // Count badge (dim text). Entities → total; Renewals → urgent + past
        // (severity red OR daysUntil ≤ 30); Regulatory Watch → open alerts.
        const navCount =
          it.to === "/entities"
            ? counts.entities
            : it.to === "/renewals"
              ? counts.renewals
              : it.to === "/regulatory-watch"
                ? counts.regwatch
                : undefined;

        // Distinct aria-label per badge so screen readers announce what the
        // number refers to.
        const badgeAriaLabel =
          it.to === "/entities"
            ? `${navCount} entities`
            : it.to === "/renewals"
              ? `${navCount} renewals due`
              : `${navCount} regulatory alerts`;

        const navLink = (
          <NavLink
            to={it.to}
            end={it.to === "/"}
            onClick={onItemClick}
            className={({ isActive }) =>
              cn(
                // Proto .nav-item
                "flex items-center gap-[10px] h-9 px-[11px] rounded-[8px]",
                "text-[13px] font-medium transition-colors",
                // Mobile touch target
                "min-h-[44px] sm:min-h-[36px]",
                isActive
                  ? // Proto .nav-item.active
                    "bg-accent-soft text-accent font-semibold"
                  : "text-text hover:bg-surface-2",
              )
            }
          >
            {({ isActive }) => (
              <>
                {/* Proto .nav-item .ic */}
                <it.icon
                  className={cn(
                    "h-4 w-4 shrink-0",
                    isActive ? "text-accent" : "text-muted",
                  )}
                  aria-hidden
                />
                <span className="truncate">{t(it.labelKey)}</span>

                {/* Count badge — shown only when > 0.
                    Entities → dim informational total (not an attention count).
                    Renewals / Regulatory Watch → red circular notification pill. */}
                {navCount !== undefined && navCount > 0 && (
                  it.to === "/entities" ? (
                    <span className="ml-auto text-[11px] text-dim" aria-label={badgeAriaLabel}>
                      {navCount}
                    </span>
                  ) : (
                    <span
                      className={cn(
                        "ml-auto inline-flex items-center justify-center shrink-0",
                        "min-w-[18px] h-[18px] px-1 rounded-full",
                        "bg-sev-expired text-white text-[10px] font-semibold leading-none",
                      )}
                      aria-label={badgeAriaLabel}
                    >
                      {navCount}
                    </span>
                  )
                )}
              </>
            )}
          </NavLink>
        );

        return (
          <div key={it.to}>
            {showComplianceHeader && (
              // Proto .nav-group
              <p className="text-[10px] tracking-[.08em] uppercase text-dim font-bold px-[10px] pt-[13px] pb-[5px]">
                {t("group_compliance")}
              </p>
            )}
            {navLink}
          </div>
        );
      })}
    </nav>
  );
}

// ── LeftNav ───────────────────────────────────────────────────────────────────

export function LeftNav({
  open,
  onClose,
  canSeeAdmin,
  canSeeAuditLog,
  canSeeDeletedEntities,
}: LeftNavProps): JSX.Element {
  const { t } = useTranslation("nav");
  return (
    <>
      {/* Desktop persistent rail — proto .side */}
      <aside
        aria-label={t("primary_navigation")}
        className={cn(
          "hidden lg:flex shrink-0 bg-surface border-r border-border",
          // Proto: 224px (w-56) instead of previous w-60
          "w-56 h-[calc(100vh-3.5rem)] sticky top-14 flex-col",
        )}
      >
        {/* Brand lives only in the top bar (Header.tsx) — not duplicated here. */}
        {/* Proto .nav: flex-1 overflow-auto padding */}
        <div className="flex-1 overflow-auto py-2 px-[10px]">
          <NavList
            canSeeAdmin={canSeeAdmin}
            canSeeAuditLog={canSeeAuditLog}
            canSeeDeletedEntities={canSeeDeletedEntities}
          />
        </div>
      </aside>

      {/* Mobile drawer */}
      <Dialog.Root open={open} onOpenChange={(v) => !v && onClose()}>
        <Dialog.Portal>
          <Dialog.Overlay className="lg:hidden fixed inset-0 z-40 bg-black/35 animate-in fade-in" />
          <Dialog.Content
            className={cn(
              "lg:hidden fixed left-0 top-0 z-50 h-full bg-surface",
              "border-r border-border safe-top safe-bottom flex flex-col",
              // Match desktop width on mobile too
              "w-64 focus:outline-none",
            )}
          >
            {/* Drawer header: brand block + close */}
            <div className="flex items-center justify-between pr-2 shrink-0">
              <BrandBlock />
              <Dialog.Close
                aria-label={t("close_navigation")}
                className="h-11 w-11 inline-flex items-center justify-center rounded-lg hover:bg-surface-2 text-muted shrink-0"
              >
                <X className="h-5 w-5" aria-hidden />
              </Dialog.Close>
            </div>

            <div className="h-px bg-border-2 mx-3 shrink-0" aria-hidden />

            {/* Nav content */}
            <div className="flex-1 overflow-auto py-1 px-[10px]">
              {/* Mobile-specific page title — screen-reader only */}
              <Dialog.Title className="sr-only">{t("navigation")}</Dialog.Title>
              <NavList
                canSeeAdmin={canSeeAdmin}
                canSeeAuditLog={canSeeAuditLog}
                canSeeDeletedEntities={canSeeDeletedEntities}
                onItemClick={onClose}
              />
            </div>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
    </>
  );
}
